import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, rest } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryClient';
import { useLanguage } from '@/features/i18n';
import { applyTranslations, applyTranslationsToArray } from '@/lib/utils/translateContent';
import type { Service } from '@/types/database';

export function useServices(options?: { includeUnpublished?: boolean }) {
  const { language } = useLanguage();

  return useQuery({
    queryKey: [...queryKeys.services.list(), options, language],
    queryFn: async () => {
      let query = rest.from<Service>('services')
        .select('*')
        .order('order_index', { ascending: true });

      if (!options?.includeUnpublished) {
        query = query.eq('status', 'published');
      }

      const { data, error } = await query;
      if (error) throw error;

      // Apply translations based on current language
      return applyTranslationsToArray(data as Service[], language);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useService(slug: string) {
  const { language } = useLanguage();

  return useQuery({
    queryKey: [...queryKeys.services.detail(slug), language],
    queryFn: async () => {
      const { data, error } = await rest.from<Service>('services')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;

      // Apply translations based on current language
      return applyTranslations(data as Service, language);
    },
    enabled: !!slug,
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (service: Omit<Service, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('services')
        .insert(service as any)
        .select()
        .single();

      if (error) throw error;
      return data as Service;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
    },
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Service> & { id: string }) => {
      const { data, error } = await supabase
        .from('services')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Service;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
      if (data?.slug) {
        queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(data.slug) });
      }
    },
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
    },
  });
}
